import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";

import { copyTextToClipboard } from "../../../../helpers/clipboard";
import { formatMoneyFromCents } from "../../../../helpers/money";
import {
  downloadCommissionSummaryPdf,
  getCommissionShareText,
  getCommissionSummary,
  markCommissionsPaidOut,
  ProfessionalCommissionSummary,
  triggerCommissionPdfDownload,
  unmarkCommissionsPaidOut,
} from "../../../../services/professionalService";

interface ProfessionalCommissionSummaryPanelProps {
  professionalId: number;
  professionalName?: string;
}

type StatusFilter = "" | "accrued" | "paid_out";
type ItemTypeFilter = "" | "service" | "retail";

const currentMonthValue = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");

  return `${now.getFullYear()}-${month}`;
};

const formatCommissionRateBps = (
  bps: number | null | undefined,
  notAvailable: string
): string => {
  if (bps === null || bps === undefined) {
    return notAvailable;
  }

  const value = bps / 100;
  const formatted = Number.isInteger(value)
    ? String(value)
    : value.toFixed(2).replace(/\.?0+$/, "");

  return `${formatted}%`;
};

const CommissionSummarySkeleton = () => (
  <>
    <Row className="g-3 mb-4 placeholder-glow">
      {[0, 1, 2, 3].map((key) => (
        <Col sm={3} key={key}>
          <Card className="border shadow-none mb-0">
            <CardBody>
              <span className="placeholder col-6 mb-2 d-block" />
              <span className="placeholder col-4 d-block" style={{ height: 28 }} />
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
    <div className="table-responsive placeholder-glow">
      <Table className="align-middle table-nowrap mb-0">
        <thead className="table-light">
          <tr>
            {[0, 1, 2, 3, 4, 5, 6].map((key) => (
              <th key={key}>
                <span className="placeholder col-8" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[0, 1, 2].map((row) => (
            <tr key={row}>
              {[0, 1, 2, 3, 4, 5, 6].map((col) => (
                <td key={col}>
                  <span className="placeholder col-10" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  </>
);

const escapeCsvCell = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

const ProfessionalCommissionSummaryPanel = ({
  professionalId,
  professionalName,
}: ProfessionalCommissionSummaryPanelProps) => {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(currentMonthValue);
  const [serviceFilter, setServiceFilter] = useState("");
  const [specialOnly, setSpecialOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [itemTypeFilter, setItemTypeFilter] = useState<ItemTypeFilter>("");
  const [summary, setSummary] = useState<ProfessionalCommissionSummary | null>(null);
  const [serviceOptions, setServiceOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [sharePreviewOpen, setSharePreviewOpen] = useState(false);
  const [sharePreviewText, setSharePreviewText] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const notAvailable = t("professionals.common.notAvailable");
  const trackingOff = summary?.use_commission_tracking === false;

  const formatMoney = useCallback(
    (cents: number, currency?: string) =>
      formatMoneyFromCents(cents, i18n.language, currency ?? summary?.currency ?? "BRL"),
    [i18n.language, summary?.currency]
  );

  const formatDateTime = useCallback(
    (value: string | null | undefined) => {
      if (!value) {
        return notAvailable;
      }

      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    },
    [i18n.language, notAvailable]
  );

  const loadSummary = useCallback(async () => {
    setIsLoading(true);

    try {
      const serviceId = Number(serviceFilter) || null;
      const response = await getCommissionSummary(professionalId, month, {
        service_id: serviceId,
        special_only: specialOnly,
        status: statusFilter || "all",
      });
      setSummary(response.data);
      setSelectedIds([]);

      if (!serviceId && !specialOnly && !statusFilter) {
        const nextOptions = new Map<number, string>();
        for (const item of response.data?.items ?? []) {
          if (item.service_id && item.service_name) {
            nextOptions.set(item.service_id, item.service_name);
          }
        }
        setServiceOptions(
          Array.from(nextOptions.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name, i18n.language))
        );
      }
    } catch (error: any) {
      setSummary(null);
      toast.error(error?.data || t("studio.professionals.commissions.toast.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [professionalId, month, serviceFilter, specialOnly, statusFilter, i18n.language, t]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const allItems = useMemo(() => summary?.items ?? [], [summary]);

  const items = useMemo(() => {
    if (!itemTypeFilter) {
      return allItems;
    }

    return allItems.filter((item) => {
      if (itemTypeFilter === "retail") {
        return item.item_type === "retail";
      }

      return item.item_type !== "retail";
    });
  }, [allItems, itemTypeFilter]);

  const clientLinesSum = useMemo(
    () => items.reduce((acc, item) => acc + (item.commission_amount ?? 0), 0),
    [items]
  );

  const periodTotal = itemTypeFilter
    ? clientLinesSum
    : (summary?.summary.total_commission_cents ?? 0);
  const apiLinesSum = itemTypeFilter
    ? clientLinesSum
    : (summary?.summary.lines_sum_cents ?? clientLinesSum);
  const accruedCents = itemTypeFilter
    ? items
        .filter((item) => (item.status ?? "accrued") === "accrued")
        .reduce((acc, item) => acc + (item.commission_amount ?? 0), 0)
    : (summary?.summary.accrued_cents ?? 0);
  const paidOutCents = itemTypeFilter
    ? items
        .filter((item) => item.status === "paid_out")
        .reduce((acc, item) => acc + (item.commission_amount ?? 0), 0)
    : (summary?.summary.paid_out_cents ?? 0);
  const totalsMatch = itemTypeFilter
    ? true
    : summary?.summary.totals_match !== false &&
      clientLinesSum === periodTotal &&
      apiLinesSum === periodTotal;

  const accruedSelectableIds = useMemo(
    () =>
      items
        .filter((item) => (item.status ?? "accrued") === "accrued")
        .map((item) => item.order_item_id),
    [items]
  );

  const paidOutSelectableIds = useMemo(
    () =>
      items
        .filter((item) => item.status === "paid_out")
        .map((item) => item.order_item_id),
    [items]
  );

  const allAccruedSelected =
    accruedSelectableIds.length > 0 &&
    accruedSelectableIds.every((id) => selectedIds.includes(id));

  const toggleSelectAllAccrued = () => {
    if (allAccruedSelected) {
      setSelectedIds((prev) => prev.filter((id) => !accruedSelectableIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...accruedSelectableIds])));
  };

  const toggleRow = (orderItemId: number) => {
    setSelectedIds((prev) =>
      prev.includes(orderItemId)
        ? prev.filter((id) => id !== orderItemId)
        : [...prev, orderItemId]
    );
  };

  const handleMarkSelected = async () => {
    const ids = selectedIds.filter((id) => accruedSelectableIds.includes(id));
    if (ids.length === 0) {
      toast.info(t("studio.professionals.commissions.toast.selectAccrued"));
      return;
    }

    setIsMutating(true);
    try {
      const response = await markCommissionsPaidOut(professionalId, {
        order_item_ids: ids,
      });
      toast.success(
        t("studio.professionals.commissions.toast.markPaidOutSuccess", {
          count: response.data?.marked_count ?? ids.length,
        })
      );
      await loadSummary();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.commissions.toast.markPaidOutError")
      );
    } finally {
      setIsMutating(false);
    }
  };

  const handleMarkMonth = async () => {
    setIsMutating(true);
    try {
      const response = await markCommissionsPaidOut(professionalId, {
        month,
        mark_all_accrued: true,
      });
      toast.success(
        t("studio.professionals.commissions.toast.markPaidOutSuccess", {
          count: response.data?.marked_count ?? 0,
        })
      );
      await loadSummary();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.commissions.toast.markPaidOutError")
      );
    } finally {
      setIsMutating(false);
    }
  };

  const handleUnmarkSelected = async () => {
    const ids = selectedIds.filter((id) => paidOutSelectableIds.includes(id));
    if (ids.length === 0) {
      toast.info(t("studio.professionals.commissions.toast.selectPaidOut"));
      return;
    }

    setIsMutating(true);
    try {
      const response = await unmarkCommissionsPaidOut(professionalId, {
        order_item_ids: ids,
      });
      toast.success(
        t("studio.professionals.commissions.toast.unmarkPaidOutSuccess", {
          count: response.data?.unmarked_count ?? ids.length,
        })
      );
      await loadSummary();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.commissions.toast.unmarkPaidOutError")
      );
    } finally {
      setIsMutating(false);
    }
  };

  const fetchShareText = useCallback(async (): Promise<string | null> => {
    try {
      const response = await getCommissionShareText(professionalId, month);
      const text = response.data?.text ?? "";
      if (!text || (response.data?.line_count ?? 0) === 0) {
        toast.info(t("studio.professionals.commissions.empty"));
        return null;
      }
      return text;
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.commissions.toast.shareError")
      );
      return null;
    }
  }, [month, professionalId, t]);

  const handleOpenSharePreview = useCallback(async () => {
    if (items.length === 0 || isSharing) {
      return;
    }
    setIsSharing(true);
    try {
      const text = await fetchShareText();
      if (!text) {
        return;
      }
      setSharePreviewText(text);
      setSharePreviewOpen(true);
    } finally {
      setIsSharing(false);
    }
  }, [fetchShareText, isSharing, items.length]);

  const handleCopyShareText = useCallback(async () => {
    const text = sharePreviewText || (await fetchShareText());
    if (!text) {
      return;
    }
    const copied = await copyTextToClipboard(text);
    if (copied) {
      toast.success(t("studio.professionals.commissions.toast.copyShareSuccess"));
    } else {
      toast.error(t("studio.professionals.commissions.toast.copyShareError"));
    }
  }, [fetchShareText, sharePreviewText, t]);

  const handleWhatsAppShare = useCallback(() => {
    const text = sharePreviewText;
    if (!text) {
      return;
    }
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [sharePreviewText]);

  const handleDownloadPdf = useCallback(async () => {
    if (items.length === 0 || isSharing) {
      return;
    }
    setIsSharing(true);
    try {
      const blob = await downloadCommissionSummaryPdf(professionalId, month);
      const slug = (professionalName ?? `profissional-${professionalId}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-|-$/g, "");
      triggerCommissionPdfDownload(blob, `comissao-${slug}-${month}.pdf`);
      toast.success(t("studio.professionals.commissions.toast.pdfSuccess"));
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.professionals.commissions.toast.pdfError")
      );
    } finally {
      setIsSharing(false);
    }
  }, [isSharing, items.length, month, professionalId, professionalName, t]);

  const exportCsv = useCallback(() => {
    if (items.length === 0) {
      toast.info(t("studio.professionals.commissions.empty"));
      return;
    }

    const header = [
      t("studio.professionals.commissions.csv.paidAt"),
      t("studio.professionals.commissions.csv.itemType"),
      t("studio.professionals.commissions.csv.service"),
      t("studio.professionals.commissions.csv.order"),
      t("studio.professionals.commissions.csv.lineTotal"),
      t("studio.professionals.commissions.csv.ratePercent"),
      t("studio.professionals.commissions.csv.defaultPercent"),
      t("studio.professionals.commissions.csv.rateOrigin"),
      t("studio.professionals.commissions.csv.special"),
      t("studio.professionals.commissions.csv.status"),
      t("studio.professionals.commissions.csv.paidOutAt"),
      t("studio.professionals.commissions.csv.commission"),
      t("studio.professionals.commissions.csv.breakdown"),
    ].join(",");

    const defaultRate =
      summary?.default_commission_rate_bps == null
        ? ""
        : (summary.default_commission_rate_bps / 100).toFixed(2).replace(/\.?0+$/, "");

    const body = items
      .map((item) => {
        const rate =
          item.commission_rate_bps === null || item.commission_rate_bps === undefined
            ? ""
            : (item.commission_rate_bps / 100).toFixed(2).replace(/\.?0+$/, "");
        const origin =
          item.rate_origin === "service_override"
            ? t("studio.professionals.commissions.rateOrigin.serviceOverride")
            : t("studio.professionals.commissions.rateOrigin.professionalDefault");
        const statusLabel =
          item.status === "paid_out"
            ? t("studio.professionals.commissions.status.paidOut")
            : t("studio.professionals.commissions.status.accrued");
        const breakdown = t("studio.professionals.commissions.breakdown.formula", {
          base: (item.line_total_cents / 100).toFixed(2),
          rate: rate || "—",
          amount: (item.commission_amount / 100).toFixed(2),
        });
        const itemTypeLabel =
          item.item_type === "retail"
            ? t("studio.professionals.commissions.csv.itemTypeRetail")
            : item.item_type === "package"
              ? t("studio.professionals.commissions.csv.itemTypePackage")
              : t("studio.professionals.commissions.csv.itemTypeService");
        const lineName =
          item.item_type === "retail" && item.service_name
            ? `[${t("studio.professionals.commissions.productBadge")}] ${item.service_name}`
            : (item.service_name ?? "");

        return [
          escapeCsvCell(item.paid_at ? formatDateTime(item.paid_at) : ""),
          escapeCsvCell(itemTypeLabel),
          escapeCsvCell(lineName),
          String(item.order_id),
          String((item.line_total_cents / 100).toFixed(2)),
          escapeCsvCell(rate),
          escapeCsvCell(defaultRate),
          escapeCsvCell(origin),
          item.is_special_rate
            ? t("studio.professionals.commissions.csv.specialYes")
            : t("studio.professionals.commissions.csv.specialNo"),
          escapeCsvCell(statusLabel),
          escapeCsvCell(item.paid_out_at ? formatDateTime(item.paid_out_at) : ""),
          String((item.commission_amount / 100).toFixed(2)),
          escapeCsvCell(breakdown),
        ].join(",");
      })
      .join("\n");

    const footer = [
      "",
      "",
      t("studio.professionals.commissions.csv.totalLabel"),
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      String((periodTotal / 100).toFixed(2)),
      escapeCsvCell(
        t("studio.professionals.commissions.reconcile.csvNote", {
          lines: (clientLinesSum / 100).toFixed(2),
          total: (periodTotal / 100).toFixed(2),
        })
      ),
    ].join(",");

    const blob = new Blob([`\uFEFF${header}\n${body}\n${footer}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const slug = (professionalName ?? `profissional-${professionalId}`)
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "");
    link.href = url;
    link.download = `comissoes-${slug}-${month}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success(
      t("studio.professionals.commissions.toast.exportCsvSuccess", { count: items.length })
    );
  }, [
    clientLinesSum,
    formatDateTime,
    items,
    month,
    periodTotal,
    professionalId,
    professionalName,
    summary?.default_commission_rate_bps,
    t,
  ]);

  if (!isLoading && trackingOff) {
    return (
      <Alert color="info" className="mb-0">
        <h5 className="alert-heading">
          {t("studio.professionals.commissions.trackingOff.title")}
        </h5>
        <p className="mb-0">{t("studio.professionals.commissions.trackingOff.body")}</p>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-4">
        <div className="d-flex flex-wrap gap-3 flex-grow-1">
          <div style={{ maxWidth: 200, minWidth: 160 }}>
            <Label htmlFor="professional-commission-month" className="form-label">
              {t("studio.professionals.commissions.filterMonth")}
            </Label>
            <Input
              id="professional-commission-month"
              type="month"
              value={month}
              disabled={isLoading || isMutating}
              onChange={(event) => setMonth(event.target.value)}
            />
          </div>
          <div style={{ maxWidth: 220, minWidth: 160 }}>
            <Label htmlFor="professional-commission-service" className="form-label">
              {t("studio.professionals.commissions.filterService")}
            </Label>
            <Input
              id="professional-commission-service"
              type="select"
              value={serviceFilter}
              disabled={isLoading || isMutating}
              onChange={(event) => setServiceFilter(event.target.value)}
            >
              <option value="">
                {t("studio.professionals.commissions.filterServiceAll")}
              </option>
              {serviceOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Input>
          </div>
          <div style={{ maxWidth: 180, minWidth: 140 }}>
            <Label htmlFor="professional-commission-status" className="form-label">
              {t("studio.professionals.commissions.filterStatus")}
            </Label>
            <Input
              id="professional-commission-status"
              type="select"
              value={statusFilter}
              disabled={isLoading || isMutating}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            >
              <option value="">
                {t("studio.professionals.commissions.filterStatusAll")}
              </option>
              <option value="accrued">
                {t("studio.professionals.commissions.status.accrued")}
              </option>
              <option value="paid_out">
                {t("studio.professionals.commissions.status.paidOut")}
              </option>
            </Input>
          </div>
          <div style={{ maxWidth: 160, minWidth: 130 }}>
            <Label htmlFor="professional-commission-item-type" className="form-label">
              {t("studio.professionals.commissions.filterItemType")}
            </Label>
            <Input
              id="professional-commission-item-type"
              type="select"
              value={itemTypeFilter}
              disabled={isLoading || isMutating}
              onChange={(event) =>
                setItemTypeFilter(event.target.value as ItemTypeFilter)
              }
            >
              <option value="">
                {t("studio.professionals.commissions.filterItemTypeAll")}
              </option>
              <option value="service">
                {t("studio.professionals.commissions.filterItemTypeService")}
              </option>
              <option value="retail">
                {t("studio.professionals.commissions.filterItemTypeRetail")}
              </option>
            </Input>
          </div>
          <div className="form-check align-self-end mb-2">
            <Input
              id="professional-commission-special-only"
              type="checkbox"
              className="form-check-input"
              checked={specialOnly}
              disabled={isLoading || isMutating}
              onChange={(event) => setSpecialOnly(event.target.checked)}
            />
            <Label
              className="form-check-label"
              htmlFor="professional-commission-special-only"
            >
              {t("studio.professionals.commissions.filterSpecialOnly")}
            </Label>
          </div>
        </div>
        {!isLoading && items.length > 0 ? (
          <div className="d-flex flex-wrap gap-2">
            <Button
              color="primary"
              type="button"
              size="sm"
              onClick={() => void handleOpenSharePreview()}
              disabled={isMutating || isSharing}
            >
              <i className="ri-file-text-line me-1" />
              {t("studio.professionals.commissions.actions.previewShare")}
            </Button>
            <Button
              color="soft-danger"
              type="button"
              size="sm"
              onClick={() => void handleDownloadPdf()}
              disabled={isMutating || isSharing}
            >
              <i className="ri-file-pdf-line me-1" />
              {t("studio.professionals.commissions.actions.downloadPdf")}
            </Button>
            <Button
              color="soft-secondary"
              type="button"
              size="sm"
              onClick={exportCsv}
              disabled={isMutating || isSharing}
            >
              <i className="ri-file-excel-2-line me-1" />
              {t("studio.professionals.commissions.actions.exportCsv")}
            </Button>
          </div>
        ) : null}
      </div>

      <Modal
        isOpen={sharePreviewOpen}
        toggle={() => setSharePreviewOpen(false)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setSharePreviewOpen(false)}>
          {t("studio.professionals.commissions.preview.title")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            {t("studio.professionals.commissions.preview.hint")}
          </p>
          <pre
            className="bg-light border rounded p-3 mb-0"
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 360,
              overflow: "auto",
              fontFamily: "inherit",
              fontSize: 14,
            }}
          >
            {sharePreviewText}
          </pre>
        </ModalBody>
        <ModalFooter>
          <Button
            color="soft-secondary"
            type="button"
            onClick={() => setSharePreviewOpen(false)}
          >
            {t("studio.professionals.commissions.preview.close")}
          </Button>
          <Button
            color="soft-success"
            type="button"
            onClick={handleWhatsAppShare}
            disabled={!sharePreviewText}
          >
            <i className="ri-whatsapp-line me-1" />
            {t("studio.professionals.commissions.actions.whatsappShare")}
          </Button>
          <Button
            color="primary"
            type="button"
            onClick={() => void handleCopyShareText()}
            disabled={!sharePreviewText}
          >
            <i className="ri-file-copy-line me-1" />
            {t("studio.professionals.commissions.actions.copyShare")}
          </Button>
        </ModalFooter>
      </Modal>
      <p className="text-muted small mb-3">
        {t("studio.professionals.commissions.filterSpecialHint")}
      </p>

      {isLoading ? (
        <CommissionSummarySkeleton />
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col sm={3}>
              <Card className="border shadow-none mb-0">
                <CardBody>
                  <p className="text-muted mb-1 fs-13">
                    {t("studio.professionals.commissions.summary.total")}
                  </p>
                  <h4 className="mb-0 fw-semibold">
                    {formatMoney(periodTotal, summary?.currency)}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={3}>
              <Card className="border shadow-none mb-0">
                <CardBody>
                  <p className="text-muted mb-1 fs-13">
                    {t("studio.professionals.commissions.summary.accrued")}
                  </p>
                  <h4 className="mb-0 fw-semibold text-warning">
                    {formatMoney(accruedCents, summary?.currency)}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={3}>
              <Card className="border shadow-none mb-0">
                <CardBody>
                  <p className="text-muted mb-1 fs-13">
                    {t("studio.professionals.commissions.summary.paidOut")}
                  </p>
                  <h4 className="mb-0 fw-semibold text-success">
                    {formatMoney(paidOutCents, summary?.currency)}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={3}>
              <Card className="border shadow-none mb-0">
                <CardBody>
                  <p className="text-muted mb-1 fs-13">
                    {t("studio.professionals.commissions.summary.servicesCount")}
                  </p>
                  <h4 className="mb-0 fw-semibold">{summary?.summary.services_count ?? 0}</h4>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {items.length > 0 ? (
            <Alert
              color={totalsMatch ? "success" : "warning"}
              className="mb-3"
              role="status"
            >
              {totalsMatch
                ? t("studio.professionals.commissions.reconcile.match", {
                    total: formatMoney(periodTotal, summary?.currency),
                  })
                : t("studio.professionals.commissions.reconcile.mismatch")}
            </Alert>
          ) : null}

          {items.length > 0 ? (
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button
                color="success"
                size="sm"
                type="button"
                disabled={isMutating || selectedIds.filter((id) => accruedSelectableIds.includes(id)).length === 0}
                onClick={() => void handleMarkSelected()}
              >
                {t("studio.professionals.commissions.actions.markSelected")}
              </Button>
              <Button
                color="soft-success"
                size="sm"
                type="button"
                disabled={isMutating || accruedSelectableIds.length === 0}
                onClick={() => void handleMarkMonth()}
              >
                {t("studio.professionals.commissions.actions.markMonth")}
              </Button>
              <Button
                color="soft-warning"
                size="sm"
                type="button"
                disabled={isMutating || selectedIds.filter((id) => paidOutSelectableIds.includes(id)).length === 0}
                onClick={() => void handleUnmarkSelected()}
              >
                {t("studio.professionals.commissions.actions.unmarkSelected")}
              </Button>
            </div>
          ) : null}

          {items.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-1">{t("studio.professionals.commissions.empty")}</p>
              <p className="text-muted small mb-3">
                {t("studio.professionals.commissions.emptyHint")}
              </p>
              <Link to="/orders?status=paid" className="btn btn-soft-primary">
                <i className="ri-file-list-3-line align-bottom me-1" />
                {t("studio.professionals.commissions.emptyAction")}
              </Link>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 40 }}>
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        checked={allAccruedSelected}
                        disabled={isMutating || accruedSelectableIds.length === 0}
                        onChange={toggleSelectAllAccrued}
                        aria-label={t("studio.professionals.commissions.actions.selectAllAccrued")}
                      />
                    </th>
                    <th>{t("studio.professionals.commissions.table.date")}</th>
                    <th>{t("professionals.fields.service_name")}</th>
                    <th>{t("studio.professionals.commissions.table.status")}</th>
                    <th className="text-end">
                      {t("studio.professionals.commissions.table.base")}
                    </th>
                    <th className="text-end">
                      {t("studio.professionals.commissions.table.rate")}
                    </th>
                    <th className="text-end">
                      {t("studio.professionals.commissions.table.commission")}
                    </th>
                    <th className="text-end">
                      {t("studio.professionals.commissions.table.order")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const rateLabel = formatCommissionRateBps(
                      item.commission_rate_bps,
                      notAvailable
                    );
                    const isOverride =
                      item.rate_origin === "service_override" || item.is_special_rate;
                    const isPaidOut = item.status === "paid_out";

                    return (
                      <tr key={item.order_item_id}>
                        <td>
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedIds.includes(item.order_item_id)}
                            disabled={isMutating}
                            onChange={() => toggleRow(item.order_item_id)}
                          />
                        </td>
                        <td>{formatDateTime(item.paid_at)}</td>
                        <td>
                          <Link
                            to={`/orders/${item.order_id}`}
                            className="text-body fw-medium"
                            title={t("studio.professionals.commissions.table.openOrder", {
                              id: item.order_id,
                            })}
                          >
                            {item.service_name ?? notAvailable}
                          </Link>
                          {item.item_type === "retail" ? (
                            <Badge color="secondary" className="badge-soft-secondary ms-2">
                              {t("studio.professionals.commissions.productBadge")}
                            </Badge>
                          ) : null}
                          <Badge
                            color={isOverride ? "info" : "secondary"}
                            className={`badge-soft-${isOverride ? "info" : "secondary"} ms-2`}
                          >
                            {isOverride
                              ? t("studio.professionals.commissions.rateOrigin.serviceOverride")
                              : t("studio.professionals.commissions.rateOrigin.professionalDefault")}
                          </Badge>
                          <div className="small text-muted mt-1">
                            {t("studio.professionals.commissions.breakdown.formula", {
                              base: formatMoney(
                                item.breakdown?.base_cents ?? item.line_total_cents,
                                summary?.currency
                              ),
                              rate: rateLabel,
                              amount: formatMoney(item.commission_amount, summary?.currency),
                            })}
                          </div>
                          {(item.breakdown?.discount_share_cents ?? 0) > 0 ||
                          (item.breakdown?.cogs_share_cents ?? 0) > 0 ||
                          (item.breakdown?.fee_share_cents ?? 0) > 0 ? (
                            <div className="small text-muted mt-1">
                              {(item.breakdown?.discount_share_cents ?? 0) > 0 ? (
                                <div>
                                  {t(
                                    "studio.professionals.commissions.breakdown.discountShare",
                                    {
                                      amount: formatMoney(
                                        item.breakdown?.discount_share_cents ?? 0,
                                        summary?.currency
                                      ),
                                    }
                                  )}
                                </div>
                              ) : null}
                              {(item.breakdown?.cogs_share_cents ?? 0) > 0 ? (
                                <div>
                                  {t(
                                    "studio.professionals.commissions.breakdown.cogsShare",
                                    {
                                      amount: formatMoney(
                                        item.breakdown?.cogs_share_cents ?? 0,
                                        summary?.currency
                                      ),
                                    }
                                  )}
                                </div>
                              ) : null}
                              {(item.breakdown?.fee_share_cents ?? 0) > 0 ? (
                                <div>
                                  {t(
                                    "studio.professionals.commissions.breakdown.feeShare",
                                    {
                                      amount: formatMoney(
                                        item.breakdown?.fee_share_cents ?? 0,
                                        summary?.currency
                                      ),
                                    }
                                  )}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <Badge
                            color={isPaidOut ? "success" : "warning"}
                            className={`badge-soft-${isPaidOut ? "success" : "warning"}`}
                          >
                            {isPaidOut
                              ? t("studio.professionals.commissions.status.paidOut")
                              : t("studio.professionals.commissions.status.accrued")}
                          </Badge>
                        </td>
                        <td className="text-end text-muted">
                          {formatMoney(item.line_total_cents, summary?.currency)}
                        </td>
                        <td className="text-end text-muted">{rateLabel}</td>
                        <td className="text-end fw-medium">
                          {formatMoney(item.commission_amount, summary?.currency)}
                        </td>
                        <td className="text-end">
                          <Link
                            to={`/orders/${item.order_id}`}
                            className="btn btn-sm btn-soft-primary"
                            title={t("studio.professionals.commissions.table.openOrder", {
                              id: item.order_id,
                            })}
                          >
                            #{item.order_id}
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="table-light fw-semibold">
                    <td colSpan={6} className="text-end">
                      {t("studio.professionals.commissions.table.footerSum")}
                    </td>
                    <td className="text-end">
                      {formatMoney(clientLinesSum, summary?.currency)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProfessionalCommissionSummaryPanel;
